import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Salad, Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function AcompDialog({ open, onOpenChange, target, areas, onSaved }) {
  const editing = !!target?.id;
  const [form, setForm] = useState({ nombre: "", area: "", descripcion: "", tiempo_estimado_min: "", tiene_costo: false, costo: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setForm({
        nombre: target.nombre || "",
        area: target.area || (areas[0]?.nombre || ""),
        descripcion: target.descripcion || "",
        tiempo_estimado_min: target.tiempo_estimado_min ?? "",
        tiene_costo: !!target.tiene_costo,
        costo: target.tiene_costo ? String(target.costo ?? "") : "",
      });
    } else {
      setForm({ nombre: "", area: areas[0]?.nombre || "", descripcion: "", tiempo_estimado_min: "", tiene_costo: false, costo: "" });
    }
  }, [open, target, areas]);

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Nombre requerido");
    if (!form.area) return toast.error("Área requerida");
    if (form.tiene_costo) {
      const c = parseFloat(form.costo);
      if (!Number.isFinite(c) || c < 0) return toast.error("Costo inválido");
    }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        area: form.area,
        descripcion: form.descripcion.trim() || null,
        tiempo_estimado_min: form.tiempo_estimado_min === "" ? null : parseInt(form.tiempo_estimado_min) || null,
        tiene_costo: !!form.tiene_costo,
        costo: form.tiene_costo ? parseFloat(form.costo) || 0 : 0,
      };
      if (editing) {
        await api.patch(`/acompanamientos/${target.id}`, payload);
        toast.success("Acompañamiento actualizado");
      } else {
        await api.post("/acompanamientos", payload);
        toast.success("Acompañamiento creado");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Editar acompañamiento" : "Nuevo acompañamiento"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre *</Label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Papas fritas" data-testid="acomp-nombre-input" />
          </div>
          <div>
            <Label>Área de preparación *</Label>
            <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
              <SelectTrigger data-testid="acomp-area-select"><SelectValue placeholder="Seleccionar área" /></SelectTrigger>
              <SelectContent>
                {areas.map((a) => <SelectItem key={a.id} value={a.nombre}>{a.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-0.5">El acompañamiento se enviará automáticamente a esta área.</p>
          </div>
          <div>
            <Label>Descripción interna (opcional)</Label>
            <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} />
          </div>
          <div>
            <Label>Tiempo estimado (minutos)</Label>
            <Input type="number" min="0" value={form.tiempo_estimado_min} onChange={(e) => setForm({ ...form, tiempo_estimado_min: e.target.value })} placeholder="Ej: 5" />
          </div>
          <div className="rounded-lg border border-border p-3 space-y-2">
            <label className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-bold">¿Tiene costo adicional?</div>
                <div className="text-xs text-muted-foreground">Cuando esté activo, sumará al total de la orden.</div>
              </div>
              <Switch
                checked={form.tiene_costo}
                onCheckedChange={(v) => setForm({ ...form, tiene_costo: v, costo: v ? form.costo : "" })}
                data-testid="acomp-tiene-costo-switch"
              />
            </label>
            {form.tiene_costo && (
              <div>
                <Label>Costo *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.costo}
                  onChange={(e) => setForm({ ...form, costo: e.target.value })}
                  placeholder="25.00"
                  data-testid="acomp-costo-input"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Costo unitario en MXN. Si el mesero o cliente lo deja seleccionado en la orden, se suma al total.</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="acomp-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Acompanamientos() {
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState(null);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/acompanamientos", { params: { only_active: !showInactive } }),
        api.get("/areas", { params: { only_active: true } }),
      ]);
      setItems(r1.data || []);
      setAreas(r2.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCreate = () => { setTarget(null); setDialogOpen(true); };
  const onEdit = (a) => { setTarget(a); setDialogOpen(true); };
  const onToggle = async (a) => {
    try {
      if (a.active) {
        await api.delete(`/acompanamientos/${a.id}`);
        toast.success("Acompañamiento desactivado");
      } else {
        await api.patch(`/acompanamientos/${a.id}`, { active: true });
        toast.success("Acompañamiento reactivado");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-black tracking-tight flex items-center gap-2"><Salad className="h-7 w-7 text-primary" />Acompañamientos</h2>
          <p className="text-sm text-muted-foreground mt-1">Configura guarniciones para productos del menú. Pueden ser gratis o con costo adicional.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} /> Ver inactivos
          </label>
          <Button onClick={onCreate} className="font-bold" data-testid="acomp-new-btn"><Plus className="h-4 w-4 mr-1" />Nuevo</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Salad className="h-12 w-12 mx-auto text-muted-foreground/30" />
            <div className="mt-3 text-lg font-bold">No hay acompañamientos</div>
            <p className="text-sm text-muted-foreground mt-1">Crea tu primer acompañamiento para asignarlo a productos.</p>
            <Button onClick={onCreate} className="mt-4" data-testid="acomp-empty-new-btn"><Plus className="h-4 w-4 mr-1" />Crear acompañamiento</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((a) => (
            <Card key={a.id} className={a.active ? "" : "opacity-60"} data-testid={`acomp-card-${a.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-black text-lg leading-tight truncate" title={a.nombre}>{a.nombre}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-secondary font-bold text-primary">{a.area}</span>
                      {a.tiempo_estimado_min ? <span className="ml-2">~ {a.tiempo_estimado_min} min</span> : null}
                    </div>
                    <div className="mt-1.5">
                      {a.tiene_costo ? (
                        <span className="inline-flex items-center text-xs font-black px-2 py-0.5 rounded-md bg-primary/10 text-primary" data-testid={`acomp-costo-badge-${a.id}`}>
                          +{(a.costo ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" })}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`acomp-gratis-badge-${a.id}`}>
                          Gratis
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => onEdit(a)} title="Editar" data-testid={`acomp-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => onToggle(a)} title={a.active ? "Desactivar" : "Reactivar"} data-testid={`acomp-toggle-${a.id}`}>
                      {a.active ? <Trash2 className="h-4 w-4 text-destructive" /> : <RotateCcw className="h-4 w-4 text-emerald-500" />}
                    </Button>
                  </div>
                </div>
                {a.descripcion && <p className="text-xs text-muted-foreground line-clamp-2">{a.descripcion}</p>}
                {!a.active && <div className="text-[10px] font-bold uppercase tracking-widest text-destructive">Inactivo</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AcompDialog open={dialogOpen} onOpenChange={setDialogOpen} target={target} areas={areas} onSaved={load} />
    </div>
  );
}
